import ChampionAPI from "@api/lol/Champion.api";
import SkinAPI from "@api/lol/Skin.api";
import type IProduct from "@api/models/IProduct";
import type { AppRegion } from "@core-types/region.enum";
import { useAppRegion } from "@hooks/region/useAppRegion";
import { ELoadingOptions } from "@modules/loader/useLoader.hook";
import getAvailableAccountIds from "@modules/order/hooks/getAvailableAccountIds.hook";
import { useLoLSkinProductAsBasketAccount } from "@modules/order/hooks/useBasketItem.hook";
import type IChampion from "@modules/skins/shared/models/IChampion.model";
import useBuyModalStore from "@store/buyModal.store";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import type ISkin from "../../shared/models/ISkin.model";

export const useSkinProducts = () => {
  const { changeRegion, currentRegion } = useAppRegion();
  const [champion, setChampion] = useState<IChampion | undefined>(undefined);
  const [skin, setSkin] = useState<ISkin | undefined>(undefined);
  const { slugChampion, slugSkin } = useParams();
  const [skinProducts, setSkinProducts] = useState<IProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | undefined>(
    undefined,
  );
  const { open } = useBuyModalStore();

  const { isOutOfStock, unavailableIds, getFirstAvailableId } =
    getAvailableAccountIds(selectedProduct?.accounts ?? []);

  const handleOnBuy = () => {
    if (!selectedProduct || !skin) return;

    open(
      useLoLSkinProductAsBasketAccount(
        selectedProduct,
        skin,
        getFirstAvailableId(),
        currentRegion,
      ),
    );
  };

  const handleRegionChange = (newRegion: AppRegion) => {
    changeRegion(newRegion, loadData);
  };

  // don't specify unavailableIds here to avoid useless refetching
  const loadData = useCallback(() => {
    if (!slugChampion || !slugSkin) return;

    ChampionAPI.get(slugChampion, ELoadingOptions.SKELETON).then((res) =>
      setChampion(res.data),
    );

    if (!slugSkin) return;

    SkinAPI.get(slugSkin).then((res) => setSkin(res.data));

    SkinAPI.getProduct(slugSkin).then((res) => {
      const formattedData = res.data.map((product) => ({
        ...product,
        accountIds: product.accounts.filter(
          (id) => !unavailableIds.includes(id),
        ),
      }));

      setSkinProducts(formattedData);

      const productsInStock = formattedData
        .filter((product) => product.accountIds.length > 0)
        .sort((a, b) => a.accountIds.length - b.accountIds.length);

      if (productsInStock.length <= 0) {
        setSelectedProduct(undefined);
        return;
      }

      setSelectedProduct(productsInStock[0]);
    });
  }, [slugChampion, slugSkin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSkinProducts((old) =>
      old.map((product) => ({
        ...product,
        accountIds: product.accounts.filter(
          (id) => !unavailableIds.includes(id),
        ),
      })),
    );

    if (isOutOfStock) {
      setSelectedProduct(undefined);
    }
  }, [unavailableIds, isOutOfStock]);

  return {
    skin,
    champion,
    skinProducts,
    selectedProduct,
    setSelectedProduct,
    isOutOfStock,
    unavailableIds,
    handleOnBuy,
    handleRegionChange,
  };
};
